import AppRoutes from "AppRoutes";
import { DisplayDateRelative } from "components/DisplayFields/DisplayDateRelative";
import { DisplayDocument } from "components/DisplayFields/DisplayDocument";
import { DisplayUser } from "components/DisplayFields/DisplayUser";
import { ViewPermissions } from "components/ViewPermissions";
import { createLogger } from "logging";
import { WorkingDocumentModel } from "models/WorkingDocumentModel";
import { useDocument, useDocumentsPanel, useLayout, useTranslations } from "providers";
import { useState } from "react";

import { EditDocumentName } from "./EditDocumentName";

const logger = createLogger("DocumentsPanel:ExistingDocument");

interface ExistingDocumentProps {
  document: WorkingDocumentModel;
  onRefresh?: () => void;
}

export const ExistingDocument = ({ document }: ExistingDocumentProps) => {
  const i18n = useTranslations();
  const { handleNavClick } = useLayout();
  const [isEditing, setIsEditing] = useState(false);
  const { setConfirmDeleteDocument } = useDocument();
  const { reloadMeeting } = useDocumentsPanel();

  const { id, lastModified, lastModifiedBy, canEdit, canDelete, canViewHistory, isDeleted } = document;
  logger.debug(`render:`, document, setConfirmDeleteDocument);

  const classNames = ["document"];
  if (isDeleted) classNames.push("show-deleted");

  return (
    <tr className={classNames.join(" ")} data-id={id}>
      {isEditing && (
        <EditDocumentName
          document={document}
          onRefresh={() => {
            setIsEditing(false);
            reloadMeeting();
          }}
          onCancel={() => setIsEditing(false)}
        />
      )}

      {!isEditing && (
        <>
          <td>
            <span>
              <DisplayDocument value={document} />
            </span>
          </td>
          <td className="modified">
            <DisplayDateRelative value={lastModified} />
          </td>
          <td className="modifiedBy">
            <DisplayUser value={lastModifiedBy} showTitleWithOrganisationUnit={false} />
          </td>

          <td className="actions document-status">
            <div className="actions">
              {canEdit && (
                <button
                  className="btn btn-link ml-1 global-tooltip-trigger"
                  onClick={() => setIsEditing(!isEditing)}
                  title={i18n.Actions_Edit}
                >
                  <i className="ms-Icon ms-Icon--Edit" />
                  {}
                </button>
              )}

              {canDelete && (
                <button
                  className="btn btn-link ml-1 global-tooltip-trigger"
                  onClick={() => setConfirmDeleteDocument({ id })}
                  title={i18n.Actions_Delete}
                >
                  <i className="ms-Icon ms-Icon--Delete" />
                </button>
              )}

              {canViewHistory && (
                <button
                  className="btn btn-link ml-1 global-tooltip-trigger"
                  onClick={() => {
                    const url = AppRoutes.toBackendAuditLog("WorkingDocument", id);
                    handleNavClick(url);
                  }}
                  title={i18n.Actions_View_History}
                >
                  <i className="ms-Icon ms-Icon--History" />
                </button>
              )}

              <ViewPermissions item={document} type="WorkingDocument" />
            </div>
          </td>
        </>
      )}
    </tr>
  );
};
